import { useIntl } from 'react-intl';

import type { AutocompleteSuggestionPart } from '@dynatrace-sdk/client-query';

import { DQL_DOCUMENTATION_URL_PREFIX } from '../../../../core/external-links.js';
import { ExternalLink } from '../../../../typography/external-link/ExternalLink.js';
import { Text } from '../../../../typography/text/Text.js';
import { dqlEditorMessages as messages } from '../messages.js';

const DOCUMENTATION_URL_PARTS = new Map([
  ['COMMAND_NAME', 'command'],
  ['FUNCTION_NAME', 'function'],
  ['TIMESERIES_AGGREGATION', 'timeseries'],
]);

/**
 * Generates a documentation URL for a DQL suggestion part, or `undefined` if
 * the part's token type has no corresponding docs page.
 * @param suggestion - the autocomplete suggestion part as returned by the endpoint
 */
export function generateDocumentationLink(
  suggestion: AutocompleteSuggestionPart | undefined,
): string | undefined {
  const urlPart =
    suggestion?.type && DOCUMENTATION_URL_PARTS.get(suggestion.type);
  if (!urlPart) {
    return undefined;
  }

  const encoded = encodeURI(`${urlPart}_${suggestion.suggestion}`);
  return `${DQL_DOCUMENTATION_URL_PREFIX}${encoded}`;
}

/**
 * Renders a small "Documentation" external link for a DQL suggestion part,
 * or nothing when the part's token type has no associated docs page.
 */
export const DocumentationLink = ({
  suggestionPart,
}: {
  suggestionPart: AutocompleteSuggestionPart | undefined;
}) => {
  const intl = useIntl();
  const docUrl = generateDocumentationLink(suggestionPart);
  return docUrl ? (
    <Text textStyle="small">
      <ExternalLink href={docUrl} data-dt-name="LearnMore">
        {intl.formatMessage(messages.suggestionDocumentationLink)}
      </ExternalLink>
    </Text>
  ) : null;
};
